/**
 * sandboxAnalysis — client-side analysis engine for sandbox mode.
 *
 * SAFETY: This never calls the production backend.
 * All analysis is computed locally using the scenario data.
 * No data leaves the browser session.
 *
 * SYNC: When a published framework config exists (from the internal
 * ZT sync system), this engine uses real capability names and gap
 * descriptions. Otherwise it falls back to defaults.
 */

import {
  MATURITY_SCORES,
  MATURITY_LABELS,
  PILLAR_NAMES,
  DEFAULT_GAP_TEMPLATES,
  SANDBOX_CONFIG_KEY,
} from '../../config/zeroTrustConstants';

/**
 * Read the published sandbox config (set by SandboxSyncPanel on ZT page).
 */
function getPublishedConfig() {
  try {
    const raw = localStorage.getItem(SANDBOX_CONFIG_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

/**
 * Get gap templates — prefer published config, fall back to defaults.
 */
function getGapTemplates() {
  const config = getPublishedConfig();
  if (config?.gapTemplates) return config.gapTemplates;
  return DEFAULT_GAP_TEMPLATES;
}

/**
 * Get pillar names — prefer published config, fall back to defaults.
 */
function getPillarNames() {
  const config = getPublishedConfig();
  if (config?.pillars) {
    return Object.fromEntries(config.pillars.map(p => [p.code, p.name]));
  }
  return PILLAR_NAMES;
}

/**
 * Get all pillar codes — prefer published config, fall back to defaults.
 */
function getAllPillars() {
  const config = getPublishedConfig();
  if (config?.pillars) {
    return config.pillars.map(p => p.code);
  }
  return ['IDENTITY', 'DEVICE', 'NETWORK', 'APPLICATION', 'DATA', 'VISIBILITY'];
}

/**
 * Generate a synthetic Zero Trust maturity analysis from sandbox state.
 * Returns a structure matching the production ExecutiveSummary expectations.
 */
export function generateAnalysis({ assessments, answers, techStack, complianceNeeds, orgType, riskProfile }) {
  const pillarNames = getPillarNames();
  const allPillars = getAllPillars();
  const gapTemplates = getGapTemplates();

  // Build pillar scores from capability-level assessments
  const pillarScores = {};

  allPillars.forEach(pillar => {
    // Look for capability-level assessments (capKey format: "PILLAR:capId")
    const capAssessments = assessments.filter(a => a.pillar === pillar && a.capKey);
    if (capAssessments.length > 0) {
      const sum = capAssessments.reduce((s, a) => s + (MATURITY_SCORES[a.maturity] || 1), 0);
      pillarScores[pillar] = sum / capAssessments.length;
    } else {
      // Fallback: try pillar-level assessment (legacy)
      const pillarAssessment = assessments.find(a => a.pillar === pillar && !a.capKey);
      if (pillarAssessment) {
        pillarScores[pillar] = MATURITY_SCORES[pillarAssessment.maturity] || 1;
      } else {
        pillarScores[pillar] = deriveScoreFromAnswers(pillar, answers);
      }
    }
  });

  // Overall maturity
  const scores = Object.values(pillarScores);
  const avgScore = scores.reduce((a, b) => a + b, 0) / scores.length;
  const roundedScore = Math.round(avgScore);
  const readinessLevel = MATURITY_LABELS[roundedScore] || 'TRADITIONAL';

  // Generate gaps
  const gaps = generateGaps(pillarScores, assessments, complianceNeeds, gapTemplates, pillarNames);

  // Get published config for capability count
  const config = getPublishedConfig();
  const totalCapabilities = config?.capabilityCount || allPillars.length * 4;

  // Build summary
  const summary = {
    readinessLevel,
    overallScore: avgScore,
    pillarScores: allPillars.map(p => ({
      pillar: p,
      pillarName: pillarNames[p] || p,
      score: pillarScores[p],
      maturity: MATURITY_LABELS[pillarScores[p]] || 'TRADITIONAL',
    })),
    totalCapabilities,
    assessedCapabilities: assessments.filter(a => a.maturity && a.maturity !== 'TRADITIONAL').length || Object.keys(answers).length,
    criticalGaps: gaps.filter(g => g.priority === 'CRITICAL').length,
    highGaps: gaps.filter(g => g.priority === 'HIGH').length,
    recommendations: generateRecommendations(pillarScores, techStack, complianceNeeds, riskProfile, pillarNames),
    orgType,
    techStack,
    complianceNeeds,
    publishedConfigVersion: config?.version || null,
  };

  return {
    summary,
    gaps: {
      totalGaps: gaps.length,
      criticalGaps: gaps.filter(g => g.priority === 'CRITICAL').length,
      gapsByPillar: allPillars.map(p => ({
        pillar: p,
        pillarName: pillarNames[p] || p,
        gaps: gaps.filter(g => g.pillar === p),
        count: gaps.filter(g => g.pillar === p).length,
      })),
    },
    assessments: allPillars.map(p => {
      const a = assessments.find(x => x.pillar === p);
      return {
        zt_capability_id: p,
        pillar_code: p,
        maturity_level: a?.maturity || MATURITY_LABELS[pillarScores[p]] || 'TRADITIONAL',
        implementation_status: a?.status || 'NOT_STARTED',
        priority: a?.priority || 'MEDIUM',
      };
    }),
  };
}

function deriveScoreFromAnswers(pillar, answers) {
  const prefix = pillar.toLowerCase().replace('visibility', 'visibility');
  const relevant = Object.entries(answers).filter(([k]) => k.startsWith(prefix.substring(0, 4)));

  if (relevant.length === 0) return 1;

  const valueScores = { no: 1, basic: 1.5, partial: 2, scheduled: 2, manual: 1.5, yes: 3, advanced: 3.5, automated: 4 };
  const total = relevant.reduce((sum, [, v]) => sum + (valueScores[v] || 1), 0);
  return Math.min(4, Math.max(1, Math.round(total / relevant.length)));
}

function generateGaps(pillarScores, assessments, complianceNeeds, gapTemplates, pillarNames) {
  const gaps = [];

  Object.entries(pillarScores).forEach(([pillar, score]) => {
    if (score >= 4) return; // Optimal — no gaps
    const templates = gapTemplates[pillar] || [];
    const gapCount = score <= 1 ? 3 : score <= 2 ? 2 : 1;
    const assessment = assessments.find(a => a.pillar === pillar);

    templates.slice(0, gapCount).forEach(tmpl => {
      gaps.push({
        pillar,
        pillarName: pillarNames[pillar] || pillar,
        capability: tmpl.capability,
        description: tmpl.description,
        priority: assessment?.priority || (score <= 1 ? 'CRITICAL' : score <= 2 ? 'HIGH' : 'MEDIUM'),
        currentMaturity: MATURITY_LABELS[score] || 'TRADITIONAL',
        targetMaturity: 'ADVANCED',
      });
    });
  });

  return gaps;
}

function generateRecommendations(pillarScores, techStack, complianceNeeds, riskProfile, pillarNames) {
  const recs = [];

  // Find the weakest pillars
  const sorted = Object.entries(pillarScores).sort(([, a], [, b]) => a - b);

  sorted.slice(0, 3).forEach(([pillar, score]) => {
    if (score >= 3) return;
    const pillarName = pillarNames[pillar] || pillar;
    recs.push({
      pillar,
      pillarName,
      priority: score <= 1 ? 'Critical' : 'High',
      recommendation: getRecommendation(pillar, score, techStack),
      estimatedEffort: score <= 1 ? '3-6 months' : '1-3 months',
    });
  });

  // Add compliance-specific recommendations
  if (complianceNeeds.some(f => f.includes('HIPAA') || f.includes('HITRUST'))) {
    recs.push({
      pillar: 'DATA',
      pillarName: 'Data Protection',
      priority: 'Critical',
      recommendation: 'Implement PHI-specific data classification and encryption controls to meet healthcare compliance requirements.',
      estimatedEffort: '2-4 months',
    });
  }

  if (complianceNeeds.some(f => f.includes('CMMC') || f.includes('NIST 800-171'))) {
    recs.push({
      pillar: 'ALL',
      pillarName: 'Cross-Domain',
      priority: 'Critical',
      recommendation: 'Establish CMMC Level 2 control mapping with evidence collection for all 110 practices across 14 domains.',
      estimatedEffort: '6-12 months',
    });
  }

  return recs;
}

function getRecommendation(pillar, score, techStack) {
  const recs = {
    IDENTITY: 'Deploy enterprise MFA and SSO across all user populations. Implement just-in-time privileged access with session recording.',
    DEVICE: 'Roll out EDR/XDR solution with device compliance checks enforced at access time. Automate patch management.',
    NETWORK: 'Implement micro-segmentation and replace VPN with ZTNA. Encrypt all internal east-west traffic.',
    APPLICATION: 'Integrate SAST/DAST into CI/CD pipeline. Implement API gateway with authentication and rate limiting.',
    DATA: 'Deploy automated data classification and DLP controls. Ensure encryption at rest and in transit for all sensitive data stores.',
    VISIBILITY: 'Implement SIEM with automated alerting and SOAR playbooks. Integrate threat intelligence feeds.',
  };
  return recs[pillar] || 'Improve maturity across this domain through targeted capability investments.';
}
